import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Target, Eye } from 'lucide-react';
import { company } from '@/data/company';

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    { icon: Target, title: 'Our Mission', description: company.mission },
    { icon: Eye, title: 'Our Vision', description: company.vision },
  ];

  return (
    <section ref={ref} className="py-24 relative bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-[#398024] tracking-wider uppercase mb-3">About MAYAN</h2>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Engineering Excellence Since {company.foundedYear}
          </h3>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {company.about[0]}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:border-[#398024] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#398024] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <feature.icon className="text-white" size={28} />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h4>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 bg-white border border-gray-200 rounded-2xl p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h4 className="text-3xl font-bold text-gray-900 mb-4">From concept through completion</h4>
              <p className="text-gray-600 leading-relaxed mb-4">{company.about[1]}</p>
              <p className="text-gray-600 leading-relaxed mb-6">{company.about[2]}</p>
              <ul className="space-y-3">
                {[
                  'Inspection, studies, and consulting services',
                  'Joint delivery with world-leading engineering firms',
                  'In-house engineering for smaller projects',
                  company.grade,
                ].map((item) => (
                  <li key={item} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-[#398024]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 bg-[#398024] rounded-full" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="/assets/content/engineering.jpg"
                alt="Industrial power generation equipment"
                width={1500}
                height={712}
                className="w-full h-full object-cover rounded-xl border border-gray-200"
                loading="lazy"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
